import {
  Alert,
  Box,
  Button,
  Checkbox,
  Flex,
  Group,
  Stack,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconCheck, IconShieldCheck } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BrandLogo } from "../components/BrandLogo";
import { CandidateProfileFields, CandidateReviewSummary } from "../components/CandidateProfileFields";
import { axioTokens } from "../theme";
import {
  INTAKE_MIN_SKILLS,
  INTAKE_MIN_SKILLS_WITH_RESUME,
  INTAKE_SEGMENT_DESCRIPTION,
  INTAKE_SEGMENT_LABEL,
} from "../constants/matching";
import { LoadingOverlay } from "../components/ui";
import { PhoneInput } from "../components/PhoneInput";
import { ResumeParseSection } from "../components/ResumeParseSection";
import { publicSystemApplyApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import { extractResumeText, validateResumeFile } from "../lib/extractResumeText";
import { phoneOtpSentMessage } from "../lib/otpCopy";
import { validateOptionalEmail } from "../lib/email";
import {
  captureException,
  fileSizeBucket,
  resumeKindFromName,
  trackEvent,
  trackWarning,
} from "../lib/observability";
import { parsePhoneLocal, toPhoneForApi, validateIndianMobileLocal } from "../lib/phone";
import { lakhsToPaise, validateCtcLakhs } from "../lib/ctcLakhs";
import { usePublicCityCatalog } from "../lib/useCityCatalog";
import { intakeSegmentsToFlatOptions } from "../lib/useSegmentCatalog";
import { usePublicSkillCatalog } from "../lib/useSkillCatalog";
import {
  CANDIDATE_FORM_INITIAL,
  educationSummaryFromForm,
  mergeResumeParse,
  optionalEnglishLevel,
  optionalGender,
  valuesFromExistingProfile,
  type CandidateFormValues,
} from "../lib/candidateForm";
import styles from "./ApplyPage.module.css";

const APPLY_SHELL_LAYOUT = {
  maw: {
    base: "var(--axio-unit-440)",
    sm: "var(--axio-unit-720)",
    lg: "var(--axio-unit-880)",
  },
  pt: { base: "var(--axio-unit-20)", xs: "var(--axio-unit-28)", sm: "var(--axio-unit-36)" },
  px: { base: "var(--axio-unit-16)", xs: "var(--axio-unit-20)", sm: "var(--axio-unit-28)" },
  pb: { base: "var(--axio-unit-40)", xs: "var(--axio-unit-48)", sm: "var(--axio-unit-56)" },
} as const;

const APPLY_PANEL_PADDING = {
  base: "var(--axio-unit-20) var(--axio-unit-16)",
  sm: "var(--axio-unit-28)",
} as const;

const APPLY_BRAND_ROW = {
  direction: { base: "column-reverse", sm: "row" },
  align: "flex-start",
  justify: { base: "flex-start", sm: "space-between" },
  gap: "var(--axio-unit-20)",
} as const;

export function SystemApplyPage() {
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [otpSentTo, setOtpSentTo] = useState<string | null>(null);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeId, setResumeId] = useState<string | null>(null);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [resumeHint, setResumeHint] = useState<string | null>(null);
  const [wizardStep, setWizardStep] = useState(1);
  const [parsedFields, setParsedFields] = useState<Set<string>>(new Set());

  const applyQuery = useQuery({
    queryKey: ["public-system-apply"],
    queryFn: () => publicSystemApplyApi.get(),
    retry: false,
  });

  const { labels: skillLabels, suggestionLabels, languageSuggestionLabels } = usePublicSkillCatalog();
  const { resolveFromRaw: resolveCity } = usePublicCityCatalog();

  const segmentSelectData = useMemo(
    () => (applyQuery.data ? intakeSegmentsToFlatOptions(applyQuery.data) : []),
    [applyQuery.data],
  );

  useEffect(() => {
    document.title = "Join AxioHire talent pool | AxioHire";
  }, []);

  const form = useForm<CandidateFormValues>({
    initialValues: { ...CANDIDATE_FORM_INITIAL },
    validate: {
      full_name: (v) => (v.trim().length >= 2 ? null : "Enter your full name"),
      email: validateOptionalEmail,
      cities: (v) => (v.length > 0 ? null : "Select at least one city"),
      current_ctc_lakhs: validateCtcLakhs,
      expected_ctc_lakhs: validateCtcLakhs,
      skills: (v) => {
        const minimum = resumeId ? INTAKE_MIN_SKILLS_WITH_RESUME : INTAKE_MIN_SKILLS;
        return v.filter((s) => s.trim()).length >= minimum
          ? null
          : `Add at least ${minimum} skills (autofill from resume, then review)`;
      },
      segments: (v) =>
        resumeId || v.length > 0 ? null : "Select at least one role you're open to",
      consent_given: (v) => (v ? null : "Please confirm to continue"),
    },
  });

  const activeStep = submitted ? 4 : wizardStep;

  function validatePhoneInput(value: string): boolean {
    const error = validateIndianMobileLocal(parsePhoneLocal(value));
    if (error) {
      setPhoneError(error);
      return false;
    }
    setPhoneError(null);
    return true;
  }

  async function handleSendOtp() {
    if (!validatePhoneInput(phone)) return;
    setSendingOtp(true);
    setPhoneError(null);
    try {
      const result = await publicSystemApplyApi.requestPhoneOtp(toPhoneForApi(phone));
      setOtpSent(true);
      setOtpSentTo(result.phone_masked);
    } catch (err) {
      setPhoneError(err instanceof ApiError ? err.message : "Could not send OTP");
    } finally {
      setSendingOtp(false);
    }
  }

  function handlePhoneChange(nextPhone: string) {
    setPhone(nextPhone);
    setPhoneError(null);
    if (nextPhone !== phone) {
      setOtpSent(false);
      setOtpSentTo(null);
      setOtpCode("");
    }
  }

  async function handleVerifyOtp() {
    if (!validatePhoneInput(phone)) return;
    setVerifyingOtp(true);
    setPhoneError(null);
    try {
      const result = await publicSystemApplyApi.verifyPhoneOtp(
        toPhoneForApi(phone),
        otpCode.trim(),
      );
      setVerificationToken(result.phone_verification_token);
      setPhoneVerified(true);
      setOtpSent(false);
      setOtpCode("");
      setWizardStep(2);
      const existing = result.existing_profile;
      if (result.already_registered && existing) {
        setAlreadyRegistered(true);
        form.setValues(valuesFromExistingProfile(existing));
      } else {
        setAlreadyRegistered(false);
      }
    } catch (err) {
      setPhoneError(err instanceof ApiError ? err.message : "Could not verify OTP");
    } finally {
      setVerifyingOtp(false);
    }
  }

  async function handleResumeChange(file: File | null) {
    setResumeFile(file);
    setResumeId(null);
    setResumeError(null);
    setResumeHint(null);
    if (!file || !verificationToken) return;

    const kind = resumeKindFromName(file.name);
    const sizeBucket = fileSizeBucket(file.size);
    trackEvent("resume.selected", { kind, size_bucket: sizeBucket, flow: "system_apply" });

    const validationError = validateResumeFile(file);
    if (validationError) {
      trackWarning("resume.validation_failed", { kind, size_bucket: sizeBucket, flow: "system_apply" });
      setResumeError(validationError);
      setResumeFile(null);
      return;
    }

    setResumeBusy(true);
    const apiPhone = toPhoneForApi(phone);

    try {
      trackEvent("resume.upload_started", { kind, size_bucket: sizeBucket, flow: "system_apply" });
      const extractPromise = extractResumeText(file);
      const uploadPromise = publicSystemApplyApi.uploadResume(file, apiPhone, verificationToken);

      const [textResult, uploadResult] = await Promise.allSettled([extractPromise, uploadPromise]);

      if (uploadResult.status === "fulfilled") {
        setResumeId(uploadResult.value.resume_id);
        trackEvent("resume.upload_ok", { kind, size_bucket: sizeBucket, flow: "system_apply" });
      } else {
        const err = uploadResult.reason;
        captureException(err, {
          stage: "resume_upload",
          kind,
          size_bucket: sizeBucket,
          flow: "system_apply",
          status: err instanceof ApiError ? err.status : 0,
          error_code: err instanceof ApiError ? err.code : "upload_failed",
        });
        setResumeError(err instanceof ApiError ? err.message : "Resume upload failed");
        setResumeFile(null);
        return;
      }

      if (textResult.status === "rejected") {
        captureException(textResult.reason, {
          stage: "resume_extract",
          kind,
          size_bucket: sizeBucket,
          flow: "system_apply",
        });
        setResumeHint(
          textResult.reason instanceof Error
            ? textResult.reason.message
            : "Could not read resume text. Please fill the form manually.",
        );
        return;
      }

      try {
        trackEvent("resume.parse_started", { kind, flow: "system_apply" });
        const parsed = await publicSystemApplyApi.parseResume({
          phone: apiPhone,
          phone_verification_token: verificationToken,
          text: textResult.value,
          resume_id: uploadResult.value.resume_id,
        });
        const { next, parsedKeys } = mergeResumeParse(
          form.values,
          parsed,
          (raw) => resolveCity(raw),
          skillLabels,
        );
        form.setValues({ ...form.values, ...next });
        setParsedFields(parsedKeys);
        trackEvent("resume.parse_ok", {
          kind,
          flow: "system_apply",
          skills_count: parsed.skills?.length ?? 0,
        });
        setResumeHint("Profile details filled from your resume. Please review everything before submitting.");
        setWizardStep(3);
      } catch (err) {
        captureException(err, {
          stage: "resume_parse",
          kind,
          flow: "system_apply",
          status: err instanceof ApiError ? err.status : 0,
          error_code: err instanceof ApiError ? err.code : "parse_failed",
        });
        setResumeHint(
          err instanceof ApiError
            ? err.message
            : "Resume saved, but autofill failed. Please complete the form manually.",
        );
      }
    } finally {
      setResumeBusy(false);
    }
  }

  async function handleSubmit(values: typeof form.values) {
    if (!verificationToken) return;
    setSubmitting(true);
    setSubmitError(null);
    trackEvent("intake.submit_started", {
      flow: "system_apply",
      has_resume: Boolean(resumeId),
    });
    try {
      await publicSystemApplyApi.submit({
        full_name: values.full_name.trim(),
        phone: toPhoneForApi(phone),
        phone_verification_token: verificationToken,
        email: values.email.trim() || undefined,
        cities: values.cities,
        years_experience: values.work_status === "fresher" ? 0 : values.years_experience,
        current_ctc_paise: lakhsToPaise(Number(values.current_ctc_lakhs)),
        expected_ctc_paise: lakhsToPaise(Number(values.expected_ctc_lakhs)),
        currency: "INR",
        availability_in_days: values.availability_in_days,
        current_title: values.current_title.trim() || undefined,
        education_summary: educationSummaryFromForm(values) || undefined,
        linkedin_url: values.linkedin_url.trim() || undefined,
        skills: values.skills.map((s) => s.trim()).filter(Boolean),
        languages: values.languages.map((s) => s.trim()).filter(Boolean),
        segments: values.segments,
        gender: optionalGender(values.gender),
        work_status: values.work_status,
        education_level: values.education_level || undefined,
        college_name: values.college_name || undefined,
        degree: values.degree || undefined,
        specialization: values.specialization || undefined,
        graduation_year: values.graduation_year || undefined,
        english_level: optionalEnglishLevel(values.english_level),
        whatsapp_opt_in: values.whatsapp_opt_in,
        consent_given: values.consent_given,
        resume_id: resumeId ?? undefined,
        source_channel: searchParams.get("ch") || undefined,
        role_family_code: values.role_family_code || undefined,
      });
      trackEvent("intake.submit_ok", {
        flow: "system_apply",
        has_resume: Boolean(resumeId),
        channel: searchParams.get("ch") || undefined,
      });
      setSubmitted(true);
      window.scrollTo({ top: axioTokens.unit[0], behavior: "smooth" });
    } catch (err) {
      captureException(err, {
        stage: "intake_submit",
        flow: "system_apply",
        status: err instanceof ApiError ? err.status : 0,
        error_code: err instanceof ApiError ? err.code : "submit_failed",
      });
      setSubmitError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (applyQuery.isLoading) {
    return (
      <div className={`${styles.page} ${styles.centerState}`}>
        <LoadingOverlay />
      </div>
    );
  }

  if (applyQuery.isError) {
    const message =
      applyQuery.error instanceof ApiError
        ? applyQuery.error.message
        : "Application is not available.";
    return (
      <div className={`${styles.page} ${styles.centerState}`}>
        <Box className={styles.shell} {...APPLY_SHELL_LAYOUT}>
          <div className={styles.brand}>
            <BrandLogo variant="lockup" scheme="light" height={axioTokens.unit[36]} to={null} />
          </div>
          <Alert color="red" title="Unavailable">
            {message}
          </Alert>
        </Box>
      </div>
    );
  }

  const intake = applyQuery.data!;

  if (submitted) {
    return (
      <div className={`${styles.page} ${styles.successWrap}`}>
        <Box
          className={styles.successCard}
          w={{ base: "var(--axio-percentage-full)", sm: "var(--axio-unit-520)" }}
          p={{ base: "var(--axio-unit-36) var(--axio-unit-24)", sm: "var(--axio-unit-44) var(--axio-unit-36)" }}
        >
          <div className={styles.successBrand}>
            <BrandLogo variant="full" scheme="light" height={axioTokens.unit[56]} to={null} />
          </div>
          <div className={styles.successIcon}>
            <IconCheck size="var(--axio-unit-32)" stroke="var(--axio-stroke-width-emphasis)" />
          </div>
          <h1 className={styles.successTitle}>You&apos;re all set</h1>
          <p className={styles.successBody}>
            Your profile is in the AxioHire talent pool. When a suitable role opens up, a verified
            recruiter will reach out.
          </p>
        </Box>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Box className={styles.shell} {...APPLY_SHELL_LAYOUT}>
        <header className={styles.hero}>
          <Flex className={styles.brandRow} {...APPLY_BRAND_ROW}>
            <div className={styles.heroCopy}>
              <h1 className={styles.headline}>Join AxioHire talent pool</h1>
              <Box
                component="p"
                className={styles.lede}
                maw={{ base: "var(--axio-dimension-prose-compact)", sm: "var(--axio-dimension-prose-wide)" }}
              >
                Share your profile once. Get considered for open roles that match your skills.
              </Box>
            </div>
            <div className={styles.brand}>
              <BrandLogo variant="wordmarkTagline" scheme="light" height={axioTokens.unit[48]} to={null} />
            </div>
          </Flex>
        </header>

        <nav className={styles.progress} aria-label="Application progress">
          <div className={`${styles.progressStep} ${activeStep === 1 ? styles.progressStepActive : ""} ${phoneVerified ? styles.progressStepDone : ""}`}>
            <span className={styles.progressNum}>01</span>
            <span className={styles.progressLabel}>Verify</span>
          </div>
          <div className={`${styles.progressStep} ${activeStep === 2 ? styles.progressStepActive : ""} ${wizardStep > 2 ? styles.progressStepDone : ""}`}>
            <span className={styles.progressNum}>02</span>
            <span className={styles.progressLabel}>Import</span>
          </div>
          <div className={`${styles.progressStep} ${activeStep === 3 ? styles.progressStepActive : ""} ${wizardStep > 3 ? styles.progressStepDone : ""}`}>
            <span className={styles.progressNum}>03</span>
            <span className={styles.progressLabel}>Profile</span>
          </div>
          <div className={`${styles.progressStep} ${activeStep === 4 ? styles.progressStepActive : ""}`}>
            <span className={styles.progressNum}>04</span>
            <span className={styles.progressLabel}>Confirm</span>
          </div>
        </nav>

        {wizardStep === 1 && (
        <Box component="section" className={styles.panel} aria-labelledby="verify-heading" p={APPLY_PANEL_PADDING}>
          <div className={styles.panelHead}>
            <h2 id="verify-heading" className={styles.panelTitle}>
              Verify your mobile number
            </h2>
            <p className={styles.panelHint}>
              We confirm your number so verified AxioHire recruiters can reach you about real opportunities.
            </p>
          </div>

          <Stack gap="md">
            <PhoneInput
              label="Mobile number"
              required
              value={phone}
              onChange={handlePhoneChange}
              disabled={phoneVerified}
              error={phoneError}
              size="md"
            />

            {!phoneVerified && (
              <>
                <Button
                  color="violet"
                  size="md"
                  fullWidth
                  loading={sendingOtp}
                  onClick={handleSendOtp}
                  disabled={sendingOtp || otpSent || parsePhoneLocal(phone).length < 10}
                  leftSection={<IconShieldCheck size="var(--axio-unit-18)" />}
                >
                  {otpSent ? "Verification code sent" : "Send verification code"}
                </Button>
                {!otpSent && (
                  <p className={styles.privacyNote}>
                    Your number is used only to verify your profile and is never shown publicly.
                  </p>
                )}
                {otpSent && (
                  <>
                    <Alert variant="light" color="violet">
                      {phoneOtpSentMessage(otpSentTo ?? undefined)}
                    </Alert>
                    <TextInput
                      label="Enter 6-digit code"
                      placeholder="••••••"
                      size="md"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.currentTarget.value)}
                    />
                    <Button
                      color="green"
                      size="md"
                      fullWidth
                      loading={verifyingOtp}
                      onClick={handleVerifyOtp}
                      disabled={otpCode.trim().length < 4}
                    >
                      Confirm &amp; continue
                    </Button>
                  </>
                )}
              </>
            )}

            {phoneVerified && (
              <div className={styles.statusOk}>
                <Stack gap="var(--axio-unit-8)">
                  <Group gap="var(--axio-unit-8)" justify="center">
                    <IconCheck size="var(--axio-unit-18)" stroke="var(--axio-stroke-width-emphasis)" />
                    Mobile verified — continue with your profile below
                  </Group>
                  {alreadyRegistered && (
                    <Alert color="blue" variant="light" title="You already registered">
                      You will only be able to update your information.
                    </Alert>
                  )}
                </Stack>
              </div>
            )}
          </Stack>
        </Box>
        )}

        {wizardStep === 2 && phoneVerified && (
        <Box component="section" className={styles.panel} aria-labelledby="import-heading" p={APPLY_PANEL_PADDING}>
          <div className={styles.panelHead}>
            <h2 id="import-heading" className={styles.panelTitle}>Import resume</h2>
            <p className={styles.panelHint}>We fill your profile from the resume. You review next.</p>
          </div>
          <Stack gap={axioTokens.unit[32]}>
            <ResumeParseSection
              file={resumeFile}
              busy={resumeBusy}
              error={resumeError}
              hint={resumeHint}
              disabled={resumeBusy}
              title="Parse from resume"
              description="Drop a clear PDF or DOCX (max 5 MB). We autofill skills, education, and experience."
              busyLabel="Filling your profile…"
              onFileChange={handleResumeChange}
            />
            <Button variant="default" onClick={() => setWizardStep(3)}>
              Fill manually
            </Button>
          </Stack>
        </Box>
        )}

        {wizardStep === 3 && phoneVerified && (
        <Box component="section" className={styles.panel} aria-labelledby="profile-heading" p={APPLY_PANEL_PADDING}>
          <div className={styles.panelHead}>
            <h2 id="profile-heading" className={styles.panelTitle}>Review your profile</h2>
            <p className={styles.panelHint}>Check what we filled. Yellow fields still need you.</p>
          </div>
          <Stack gap={axioTokens.unit[32]}>
            <div className={styles.profileFields}>
              <CandidateProfileFields
                form={form}
                parsedFields={parsedFields}
                publicCatalog
                skillSuggestions={suggestionLabels}
                languageSuggestions={languageSuggestionLabels}
                segmentData={segmentSelectData}
                segmentsRequired={!resumeId}
                segmentLabel={INTAKE_SEGMENT_LABEL}
                segmentDescription={INTAKE_SEGMENT_DESCRIPTION}
              />
            </div>
            <Group justify="space-between">
              <Button variant="default" onClick={() => setWizardStep(2)}>Back</Button>
              <Button
                color="violet"
                onClick={() => {
                  const err = form.validate();
                  const { consent_given: _consent, ...rest } = err.errors;
                  if (Object.keys(rest).length > 0) return;
                  setWizardStep(4);
                }}
              >
                Next
              </Button>
            </Group>
          </Stack>
        </Box>
        )}

        {wizardStep === 4 && phoneVerified && (
        <Box component="section" className={styles.panel} aria-labelledby="confirm-heading" p={APPLY_PANEL_PADDING}>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <div className={styles.panelHead}>
              <h2 id="confirm-heading" className={styles.panelTitle}>Confirm &amp; submit</h2>
              <p className={styles.panelHint}>Join the AxioHire talent pool</p>
            </div>
            <Stack gap="sm">
              <CandidateReviewSummary values={form.values} phone={phone} phoneVerified />
              <Checkbox
                mt="md"
                size="md"
                label="I agree to share my details with verified AxioHire recruiters for matching roles."
                {...form.getInputProps("consent_given", { type: "checkbox" })}
              />
              {submitError && (
                <Alert color="red" variant="light">{submitError}</Alert>
              )}
              <Group justify="space-between" mt="sm">
                <Button variant="default" onClick={() => setWizardStep(3)}>Back</Button>
                <Button type="submit" color="violet" size="md" loading={submitting} disabled={resumeBusy}>
                  Submit my profile
                </Button>
              </Group>
            </Stack>
          </form>
        </Box>
        )}

        <footer className={styles.formFooter}>
          <p>Managed by <strong>{intake.recruiter_name}</strong></p>
          <p>Secured by AxioHire</p>
        </footer>

      </Box>
    </div>
  );
}
